/* eslint-disable vars-on-top */

/* eslint-disable no-var */
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import domainsService from '@clubmed/domains-service';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import {
  getScopeSiteSection,
  isPBFLandingPage,
  isQuotationLandingPage,
  isQuotationSuccessPage,
} from 'Tracking/utils';
import { getConfigWithNoThirdPartySupport } from './thirdParty.utils';
// Constants
// B2C
const GA4_CONTAINER_ID = 'GTM-K4T9XZJP';
const GA4_CONTAINER_ID_SERVER_SIDE = 'GTM-N83KGJBT';
const GA3_CONTAINER_ID = 'GTM-KJ3DSC';
// B2B
const SELLER_GTM_KEY = 'GTM-P9H7QS7';

const getGTMKey = ({ isSeller, isShopping, asPath }) => {
  // We doing this for compatibility between ga3 and ga4 for new and old shopping
  if (isShopping) {
    const shouldLoadShoppingGA4 =
      isPBFLandingPage(asPath) || isQuotationLandingPage(asPath) || isQuotationSuccessPage(asPath);

    if (shouldLoadShoppingGA4) {
      return GA4_CONTAINER_ID;
    }

    return GA3_CONTAINER_ID;
  }

  if (isSeller) {
    return SELLER_GTM_KEY;
  }

  return GA4_CONTAINER_ID;
};

const buildTrackingContext = (context: AppContext, queryParams: any) => {
  const isFromCmta = queryParams.isFromCmta === 'true';
  const isSellerInCustomerAccount = !!queryParams.cxo_neolid;
  const { isSeller } = context;
  const isFromSeller = queryParams.isFromSeller === 'true';
  return {
    isSeller: isSeller || isFromCmta || isSellerInCustomerAccount || isFromSeller,
  };
};

function addGTMScript(newKey) {
  const noscript = document.createElement('noscript');
  // Dynamically add an iframe
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  iframe.width = '0';
  iframe.height = '0';
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${newKey}`;
  noscript.appendChild(iframe);
  document.body?.insertBefore(noscript, document.body.firstChild);

  // Function to dynamically load the GTM script
  // eslint-disable-next-line max-params
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    var f = d.getElementsByTagName(s)[0];
    var j = d.createElement(s);
    var dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'clubMedLayer', newKey);
}

function addGTMScriptServerSide(gtmConfig) {
  const noscript = document.createElement('noscript');
  // Dynamically add an iframe
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  iframe.width = '0';
  iframe.height = '0';
  iframe.src = `https://www.clubmed.${gtmConfig.tld}/metrics/ns.html?id=${GA4_CONTAINER_ID_SERVER_SIDE}`;
  noscript.appendChild(iframe);
  document.body?.insertBefore(noscript, document.body.firstChild);

  // Function to dynamically load the GTM script
  // eslint-disable-next-line max-params
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    var f = d.getElementsByTagName(s)[0];
    var j = d.createElement(s);
    var dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src =
      `https://www.clubmed.${gtmConfig.tld}/metrics/${gtmConfig.id}.js?aw=` +
      i.replace(/^GTM-/, '') +
      dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'clubMedLayer', GA4_CONTAINER_ID_SERVER_SIDE);
}

export const GoogleTagManager = () => {
  const { query: queryParams, asPath } = useRouter();
  const context = useAppContext();
  const { pageType } = usePageContext();
  const siteSection = getScopeSiteSection({
    pageType,
  });
  const { locale } = context;
  const trackingContext = buildTrackingContext(context, queryParams);
  const { enabled } = getConfigWithNoThirdPartySupport({
    name: 'tracking',
    locale,
    queryParams,
  });
  useEffect(() => {
    // KEEP THIS IN PLACE !  Caution: This clears the entire dataLayer But Necessary to avoid duplicate GTM tags
    window.clubMedLayer?.push(window.clubMedLayer?.filter((item) => !!item['gtm.start']));
    // Update GTM key based on site section
    const newKey = getGTMKey({
      ...trackingContext,
      isShopping: siteSection === 'shopping',
      asPath,
    });
    const isKeyLoaded = document.querySelector(
      `script[src*="googletagmanager.com/gtm.js?id=${newKey}"]`,
    );
    const gtmConfig = domainsService.getGTMConfig(locale);

    if (newKey && !isKeyLoaded && enabled) {
      if (gtmConfig && !trackingContext.isSeller) {
        addGTMScriptServerSide(gtmConfig);
      }

      addGTMScript(newKey);
    }
  }, [siteSection]);
  return null;
};
